import React from "react";

// GUNDI-5639: the legacy tree is a lazily-loaded chunk. A deploy during the
// trial invalidates the chunk names a running tab still points at, so an
// import() failure there would white-screen legacy users. Reload once (guarded
// by sessionStorage so a genuinely broken chunk cannot loop) and, if the
// reloaded page fails too, show a plain recovery message.
const RELOAD_KEY = "gundi_legacy_chunk_reloaded";

// The reload is ONLY for chunk-load failures. This boundary wraps the whole
// legacy tree, so it also catches ordinary render errors — and reloading those
// is an infinite loop: the import succeeds (clearing the latch below), the
// render throws again, and round it goes. Anything that is not a chunk-load
// failure renders the fallback directly.
function isChunkLoadError(error: unknown): boolean {
    const err = error as { name?: string; message?: string } | null;
    if (!err) return false;
    if (err.name === "ChunkLoadError") return true;
    return /Loading chunk|Loading CSS chunk|dynamically imported module|error loading dynamically imported module|Importing a module script failed/i
        .test(err.message ?? "");
}

// The lazy import calls this on success so the NEXT mid-trial deploy gets its
// own retry — without it the latch survives the recovery and the second chunk
// failure in a session would land straight on the error fallback.
export function clearLegacyChunkReloadLatch() {
    try {
        sessionStorage.removeItem(RELOAD_KEY);
    } catch {
        // No storage: the latch was never set either.
    }
}

type Props = { children: React.ReactNode; fallback: React.ReactNode };
type State = { failed: boolean };

export default class LegacyChunkBoundary extends React.Component<Props, State> {
    state: State = { failed: false };

    static getDerivedStateFromError(): State {
        return { failed: true };
    }

    componentDidCatch(error: unknown) {
        if (!isChunkLoadError(error)) return;
        try {
            if (sessionStorage.getItem(RELOAD_KEY) !== "1") {
                sessionStorage.setItem(RELOAD_KEY, "1");
                window.location.reload();
            }
        } catch {
            // No storage: skip the retry rather than risk a reload loop.
        }
    }

    render() {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}
